import React, { useEffect, useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { Flame, Target, Download, Copy, X, Share2, Check, Linkedin, Link as LinkIcon } from "lucide-react";
import { Button } from "./kit";
import api, { mediaUrl } from "../lib/api";
import { toast } from "sonner";

function shade(hex, pct) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    r = Math.round(r + (pct < 0 ? r : 255 - r) * pct);
    g = Math.round(g + (pct < 0 ? g : 255 - g) * pct);
    b = Math.round(b + (pct < 0 ? b : 255 - b) * pct);
    return `rgb(${Math.max(0, Math.min(255, r))}, ${Math.max(0, Math.min(255, g))}, ${Math.max(0, Math.min(255, b))})`;
  } catch { return hex; }
}

async function logoToDataUrl(logoUrl) {
  if (!logoUrl) return null;
  try {
    const res = await api.get(logoUrl.replace(/^\/api/, ""), { responseType: "blob" });
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(res.data);
    });
  } catch {
    return mediaUrl(logoUrl);
  }
}

// kind: "goal" | "streak"
export const ShareWin = ({ kind, accent = "#106958", cohortName = "", logoUrl, payload, testid }) => {
  const [open, setOpen] = useState(false);
  const [png, setPng] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoData, setLogoData] = useState(null);
  const cardRef = useRef(null);

  const generate = useCallback(async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 120));
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, width: 1080, height: 1080 });
      setPng(url);
    } catch {
      toast.error("Couldn't build the image. Try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) { setPng(null); setCopied(false); return; }
    let active = true;
    (async () => {
      const d = await logoToDataUrl(logoUrl);
      if (!active) return;
      setLogoData(d);
      generate();
    })();
    return () => { active = false; };
  }, [open, logoUrl, generate]);

  const download = () => {
    if (!png) return;
    const a = document.createElement("a");
    a.href = png;
    a.download = `${(cohortName || "buktu").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-win.png`;
    a.click();
  };

  const copy = async () => {
    if (!png) return;
    try {
      const blob = await (await fetch(png)).blob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("Image copied — paste it anywhere.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy not supported here — use Download instead.");
    }
  };

  const linkedInText = () => {
    const tag = "#SmallBusiness #Coaching";
    const who = cohortName ? `our ${cohortName} cohort` : "our cohort";
    if (kind === "goal") {
      const p = payload || {};
      const fmt = (n) => (p.pct ? `${Math.round(n)}%` : (typeof n === "number" ? n.toLocaleString() : n));
      const reached = (p.progress ?? 0) >= 100;
      return reached
        ? `🎯 Goal reached! ${who} hit our ${p.label} target of ${fmt(p.target)}. Proud of this group's momentum. ${tag}`
        : `🎯 ${who} is at ${Math.round(p.progress || 0)}% of our ${p.label} goal (${fmt(p.cur)} of ${fmt(p.target)}). Closing in. ${tag}`;
    }
    const leader = (payload?.rows || [])[0];
    if (leader) return `🔥 Momentum in ${who} — ${leader.name} is on a ${leader.streak}-week streak! Love seeing this kind of consistency. #Accountability ${tag}`;
    return `🔥 Great week of momentum in ${who}. ${tag}`;
  };

  const winTitle = () => {
    if (kind === "goal") {
      const p = payload || {};
      const fmt = (n) => (p.pct ? `${Math.round(n)}%` : (typeof n === "number" ? n.toLocaleString() : n));
      const reached = (p.progress ?? 0) >= 100;
      return reached ? `Goal reached: ${p.label}` : `${Math.round(p.progress || 0)}% to goal: ${p.label}`;
    }
    const leader = (payload?.rows || [])[0];
    return leader ? `${leader.name} is on a ${leader.streak}-week streak` : "Top streaks this week";
  };
  const winSubtitle = () => {
    if (kind === "goal") {
      const p = payload || {};
      const fmt = (n) => (p.pct ? `${Math.round(n)}%` : (typeof n === "number" ? n.toLocaleString() : n));
      return `${fmt(p.cur)} of ${fmt(p.target)} target${cohortName ? ` · ${cohortName}` : ""}`;
    }
    return `Momentum worth celebrating${cohortName ? ` · ${cohortName}` : ""}`;
  };

  const publish = async () => {
    if (!png) throw new Error("no image");
    const res = await api.post("/coach/wins", { kind, image: png, caption: linkedInText(), title: winTitle(), subtitle: winSubtitle() });
    return `${window.location.origin}${res.data.path}`;
  };

  const shareLinkedIn = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const publicUrl = await publish();
      const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;
      if (win) win.location = li; else window.open(li, "_blank", "noopener");
      toast.success("Opening LinkedIn with your win — the card preview loads automatically.");
    } catch {
      if (win) win.close();
      toast.error("Couldn't create the share link. Try Download instead.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    setBusy(true);
    let publicUrl;
    try {
      publicUrl = await publish();
    } catch {
      toast.error("Couldn't create the link. Try Download instead.");
      setBusy(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied — paste it into any post or message.");
    } catch {
      toast.success(`Link ready: ${publicUrl}`);
    }
    setBusy(false);
  };

  const base = accent || "#106958";
  const bgTop = shade(base, 0.12);
  const bgBot = shade(base, -0.45);

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Share this win"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid var(--border-hairline)", background: "transparent", borderRadius: 999, padding: "3px 9px", cursor: "pointer", font: "var(--type-data)", fontSize: 11, color: base, transition: "background-color .15s" }}
      >
        <Share2 size={12} /> Share
      </button>

      {open && (
        <div
          data-testid={`${testid}-modal`}
          onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(10,20,16,0.55)", backdropFilter: "blur(6px)", display: "grid", placeItems: "center", padding: 20 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper-white, #fff)", borderRadius: "var(--radius-lg, 18px)", padding: 20, maxWidth: 420, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ font: "var(--type-label)", fontWeight: 700, color: "var(--text-strong)" }}>Share this win</span>
              <button data-testid={`${testid}-close`} onClick={() => setOpen(false)} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-faint)" }}><X size={18} /></button>
            </div>

            <div style={{ borderRadius: 14, overflow: "hidden", aspectRatio: "1 / 1", background: bgBot, display: "grid", placeItems: "center" }}>
              {png ? (
                <img data-testid={`${testid}-preview`} src={png} alt="Shareable win" style={{ width: "100%", height: "100%", display: "block" }} />
              ) : (
                <span style={{ color: "rgba(255,255,255,0.85)", font: "var(--type-body-sm)" }}>Building your card…</span>
              )}
            </div>

            <Button data-testid={`${testid}-linkedin`} onClick={shareLinkedIn} disabled={!png || busy} iconLeft={<Linkedin size={16} />} full style={{ marginTop: 16, background: "#0a66c2", borderColor: "#0a66c2" }}>Share to LinkedIn</Button>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Button data-testid={`${testid}-copylink`} variant="secondary" onClick={copyLink} disabled={!png || busy} iconLeft={<LinkIcon size={15} />} style={{ flex: 1 }}>Copy link</Button>
              <Button data-testid={`${testid}-download`} variant="secondary" onClick={download} disabled={!png || busy} iconLeft={<Download size={15} />} style={{ flex: 1 }}>Download</Button>
              <Button data-testid={`${testid}-copy`} variant="secondary" onClick={copy} disabled={!png || busy} iconLeft={copied ? <Check size={15} /> : <Copy size={15} />} style={{ flex: 1 }}>{copied ? "Copied" : "Image"}</Button>
            </div>
            <p style={{ font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)", marginTop: 10, textAlign: "center" }}>LinkedIn & Copy link publish a public win page — the card preview loads automatically. Only opted-in names appear.</p>
          </div>
        </div>
      )}

      {/* Off-screen capture node, rendered at full 1080px */}
      {open && (
        <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
          <div ref={cardRef} style={{ width: 1080, height: 1080, position: "relative", background: `linear-gradient(160deg, ${bgTop} 0%, ${base} 42%, ${bgBot} 100%)`, color: "#fff", fontFamily: "Inter, system-ui, sans-serif", overflow: "hidden", display: "flex", flexDirection: "column", boxSizing: "border-box", padding: 90 }}>
            <div style={{ position: "absolute", top: -160, right: -160, width: 520, height: 520, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
            <div style={{ position: "absolute", bottom: -220, left: -140, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,0,0,0.12)" }} />

            {/* header */}
            <div style={{ display: "flex", alignItems: "center", gap: 24, position: "relative", zIndex: 2 }}>
              {logoData ? (
                <img src={logoData} crossOrigin="anonymous" alt="" style={{ height: 76, maxWidth: 360, objectFit: "contain" }} />
              ) : (
                <span style={{ fontSize: 42, fontWeight: 800, letterSpacing: "-0.01em" }}>{cohortName || "My cohort"}</span>
              )}
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", zIndex: 2 }}>
              {kind === "goal" ? <GoalFace payload={payload} /> : <StreakFace payload={payload} />}
            </div>

            {/* footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
              <span style={{ fontSize: 30, fontWeight: 600, opacity: 0.92 }}>{cohortName}</span>
              <span style={{ fontSize: 26, fontWeight: 600, opacity: 0.72, letterSpacing: "0.02em" }}>Powered by Buktu</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function Chip({ icon, text }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 14, background: "rgba(255,255,255,0.16)", borderRadius: 999, padding: "16px 34px", fontSize: 38, fontWeight: 700, letterSpacing: "0.01em" }}>
      {icon}{text}
    </span>
  );
}

function GoalFace({ payload }) {
  const { label, cur, target, pct, progress } = payload || {};
  const fmt = (n) => (pct ? `${Math.round(n)}%` : (typeof n === "number" ? n.toLocaleString() : n));
  const reached = (progress ?? 0) >= 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
      <Chip icon={<Target size={44} strokeWidth={2.5} />} text={reached ? "Goal reached" : "Goal progress"} />
      <div style={{ fontSize: 112, fontWeight: 800, lineHeight: 1.0, letterSpacing: "-0.02em" }}>{fmt(cur)}</div>
      <div style={{ fontSize: 46, fontWeight: 500, opacity: 0.9 }}>{label} · target {fmt(target)}</div>
      <div>
        <div style={{ height: 34, borderRadius: 999, background: "rgba(255,255,255,0.2)", overflow: "hidden" }}>
          <div style={{ width: `${Math.max(4, Math.min(100, progress || 0))}%`, height: "100%", background: "#fff", borderRadius: 999 }} />
        </div>
        <div style={{ fontSize: 40, fontWeight: 700, marginTop: 20 }}>{Math.round(progress || 0)}% of target</div>
      </div>
    </div>
  );
}

function StreakFace({ payload }) {
  const rows = (payload?.rows || []).slice(0, 3);
  const leader = rows[0];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
      <Chip icon={<Flame size={44} strokeWidth={2.5} />} text="Top streaks this week" />
      {leader && (
        <div>
          <div style={{ fontSize: 100, fontWeight: 800, lineHeight: 1.0, letterSpacing: "-0.02em" }}>{leader.name}</div>
          <div style={{ fontSize: 54, fontWeight: 600, opacity: 0.92, marginTop: 10 }}>{leader.streak} week{leader.streak === 1 ? "" : "s"} on a roll</div>
        </div>
      )}
      {rows.length > 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 10 }}>
          {rows.slice(1).map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 22, fontSize: 44, fontWeight: 600 }}>
              <span style={{ opacity: 0.6 }}>{i + 2}.</span>
              <span style={{ flex: 1 }}>{r.name}</span>
              <span style={{ opacity: 0.9 }}>{r.streak}w</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ShareWin;
